import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import styled from "styled-components";
import { MdFileDownload } from "react-icons/md";
import { BsThreeDotsVertical, BsArrowRepeat } from "react-icons/bs";

const SettlementsTab = ({ partnerId }) => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const { AuthReducer: { auth } } = useSelector((state) => state);

  useEffect(() => {
    fetchPartnerSettlements();
  }, [partnerId]);

  const fetchPartnerSettlements = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/partner-settlements/${partnerId}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setSettlements(response.data.settlements || []);
    } catch (error) {
      console.error('Failed to fetch partner settlements');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setDateFilter("all");
  };

  const filteredSettlements = settlements.filter(settlement => {
    const matchesSearch = !searchTerm || 
      settlement.settlement_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      settlement.partner_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || settlement.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const handleExportSettlements = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/partner-settlements/${partnerId}/export`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `partner-settlements-${partnerId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const csvData = settlements.map((settlement, index) => ({
        "SL. No.": index + 1,
        "Settlement ID": `S${String(settlement.id).padStart(3, '0')}`,
        "Partner Name": settlement.partner_name,
        "Period": `${new Date(settlement.period_start).toLocaleDateString()} - ${new Date(settlement.period_end).toLocaleDateString()}`,
        "Amount Due": `Rs.${parseFloat(settlement.net_payable).toLocaleString()}`,
        "Status": settlement.status,
        "Payout Date": settlement.payout_date ? new Date(settlement.payout_date).toLocaleDateString() : 'N/A'
      }));
      const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner-settlements-${partnerId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };


  return (
    <Wrapper>
      <div className="card border-0 shadow-sm p-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
          <div>
            <h4 className="text-dark mb-1 d-flex align-items-center">
              💰 Settlement Management & Payouts
            </h4>
            <p className="text-muted mb-0">
              Track and manage all financial settlements, commission payouts, and payment history for this partner. 
              Monitor pending dues, completed transactions, and export settlement reports for accounting purposes.
            </p>
          </div>

          <div className="d-flex gap-3 mt-2 mt-md-0">
            <button className="btn btn-primary d-flex align-items-center" onClick={handleExportSettlements}>
              <MdFileDownload className="me-2" size={20} />
              Export Settlements
            </button>
          </div>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2 mb-3 filter-box">
          <input
            type="text"
            className="form-control"
            placeholder="Search ID, Partner"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select className="form-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
          <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
          </select>
          <button className="btn btn-outline-secondary d-flex align-items-center gap-1" onClick={clearFilters}>
            <BsArrowRepeat size={16} />
            Clear Filters
          </button>
        </div>

        <TableWrapper>
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Settlement ID</th>
                <th>Partner Name</th>
                <th>Period</th>
                <th>Amount Due</th>
                <th>Status</th>
                <th>Payout Date</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSettlements.map((settlement, index) => (
                <tr key={index} className={index % 4 === 3 ? 'highlighted-row' : ''}>
                  <td><strong>S{String(settlement.id).padStart(3, '0')}</strong></td>
                  <td>{settlement.partner?.businessName || settlement.partner_name}</td>
                  <td>{new Date(settlement.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - {new Date(settlement.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>₹{parseFloat(settlement.net_payable).toLocaleString()}</td>
                  <td>
                    <span className={`badge ${
                      settlement.status.toLowerCase() === 'completed' ? 'badge-paid' :
                      settlement.status.toLowerCase() === 'pending' ? 'badge-pending' :
                      'badge-processing'
                    }`}>
                      {settlement.status}
                    </span>
                  </td>
                  <td>{settlement.payout_date ? new Date(settlement.payout_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</td>
                  <td className="text-center"><BsThreeDotsVertical /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .filter-box input,
  .filter-box select {
    width: 169px !important;
  }
`;

const TableWrapper = styled.div`
  .badge {
    font-size: 0.75rem;
    border-radius: 12px;
    padding: 4px 10px;
    font-weight: 500;
  }

  .badge-paid {
    background-color: rgba(25, 135, 84, 0.15);
    color: #198754;
  }

  .badge-pending {
    background-color: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }

  .badge-processing {
    background-color: rgba(13, 202, 240, 0.2);
    color: #0dcaf0;
  }

  .highlighted-row {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .table th,
  .table td {
    vertical-align: middle;
  }

  .text-center {
    text-align: center;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dee2e6;
    text-align: center;
    vertical-align: middle;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
`;

export default SettlementsTab;